import { useState, useEffect } from "react";
import { useAuth } from "../lib/AuthContext";
import { getUserStats, getTopSearchedFoods } from "../lib/userStats";
import { getRecommendations, type FoodRecommendation } from "../lib/recommendations";

type Tab = "home" | "search" | "profile" | "meal" | "recipe" | "menu" | "grocery" | "plate" | "planner" | "admin";

interface PersonalPatternsProps {
  onNavigate: (tab: Tab) => void;
}

function getGIPillClass(level: "low" | "medium" | "high"): string {
  switch (level) {
    case "low": return "gi-pill gi-pill--low";
    case "medium": return "gi-pill gi-pill--medium";
    case "high": return "gi-pill gi-pill--high";
  }
}

export default function PersonalPatterns({ onNavigate }: PersonalPatternsProps) {
  const { user } = useAuth();
  const [recommendations, setRecommendations] = useState<FoodRecommendation[]>([]);
  const [loading, setLoading] = useState(!!user);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (user) {
      getUserStats(user.uid)
        .then((stats) => {
          const topFoods = getTopSearchedFoods(stats, 5);
          if (topFoods.length >= 2) {
            const recs = getRecommendations(topFoods);
            setRecommendations(recs.slice(0, 3));
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [user]);

  if (loading || recommendations.length === 0) {
    return null;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="section-label mb-0">Based on Your Searches</div>
        <button
          onClick={() => onNavigate("search")}
          className="text-xs text-accent font-[family-name:--font-ui] font-medium hover:underline"
        >
          Search
        </button>
      </div>

      <div>
        {recommendations.map((rec, idx) => (
          <div
            key={rec.originalFood}
            className={idx > 0 ? "border-t border-border-light" : ""}
          >
            {/* Header */}
            <button
              onClick={() => setExpanded(expanded === rec.originalFood ? null : rec.originalFood)}
              className="w-full py-3 text-left flex items-center justify-between"
            >
              <div>
                <p className="text-xs text-muted font-[family-name:--font-ui]">You often search for</p>
                <p className="text-sm font-semibold text-text capitalize">{rec.originalFood}</p>
              </div>
              <div className="flex items-center gap-2">
                {rec.originalGI && rec.originalLevel && (
                  <span className={getGIPillClass(rec.originalLevel)}>
                    GI {rec.originalGI}
                  </span>
                )}
                <svg
                  className={`w-4 h-4 text-muted transition-transform ${expanded === rec.originalFood ? "rotate-180" : ""}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2}
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            {/* Expanded content */}
            {expanded === rec.originalFood && (
              <div className="pb-3">
                <p className="text-xs text-muted font-[family-name:--font-ui] font-medium mb-2">Alternatives:</p>
                <div className="space-y-2">
                  {rec.alternatives.map((alt, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between py-1"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-text">{alt.name}</p>
                        <p className="text-xs text-muted">{alt.reason}</p>
                      </div>
                      <span className={`ml-3 flex-shrink-0 ${getGIPillClass(alt.giLevel)}`}>
                        GI {alt.gi}
                      </span>
                    </div>
                  ))}
                </div>
                {rec.tip && (
                  <p className="mt-2 text-xs text-muted italic">{rec.tip}</p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
